import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import { RefreshButton, Button, Table, Skeleton } from 'components/Elements';
import { columns } from '../components/TableColumns';
import { useTransactionSearch } from '../hooks/useTransactionSearch';
import { useDevSettings } from 'hooks/useDevSettings';
import { TransactionsList } from '../components/TransactionsList';
import { useTransactions } from '../api/getTransactions';
import { useActiveCurrencies } from 'features/finance/api';
import { useCurrencyPairs } from '../api/useCurrencyPairs';
import { GlassSearchInput } from '../components/GlassSearchInput';
import { TransactionTabs } from '../components/TransactionTabs';
import { CurrencySelector } from '../components/CurrencySelector';
import { Breadcrumbs, ContentLayout } from 'components/Layout';
import { Filters } from '../components/Filters';
import { useSocket } from 'providers/SocketProvider';

const TRANSACTION_METHODS = [
  'all',
  'merchantPay',
  'mobilemoney',
  'manualBank',
  'admin',
];
const STATUS_OPTIONS = ['all', 'successful', 'failed', 'pending'];

export const Transactions = () => {
  const navigate = useNavigate();
  const { userStatus } = useDevSettings();

  const {
    searchTerm,
    searchResults,
    handleSearch,
  } = useTransactionSearch();

  const [selectedTab, setSelectedTab] = useState('deposit');
  const [activeTransactionMethod, setActiveTransactionMethod] = useState('all');
  const [transactionStatus, setTransactionStatus] = useState('all');
  const [daysFilter, setDaysFilter] = useState('7');
  const [activeCurrency, setActiveCurrency] = useState(null);
  const [currencyPageNumber] = useState(1);
  const [transactionPageNumber] = useState(1);

  const { socket, isConnected } = useSocket();

  const { data: currenciesData, isLoading: isCurrenciesLoading } =
    useActiveCurrencies({ pageNumber: currencyPageNumber });
  const { data: currencyPairsData } = useCurrencyPairs({ pageNumber: 1 });

  useEffect(() => {
    if (isConnected) {
      socket.on('mm/deposit/pending', (data) => {
        console.log('socket data');
        console.log(data);
      });
    }
  }, [isConnected, socket]);

  useEffect(() => {
    const validMethods =
      selectedTab === 'withdraw'
        ? TRANSACTION_METHODS.filter((m) => m !== 'merchantPay')
        : TRANSACTION_METHODS;

    const validStatuses = ['swap', 'transfer'].includes(selectedTab)
      ? ['successful', 'failed']
      : STATUS_OPTIONS;

    if (!validMethods.includes(activeTransactionMethod)) {
      setActiveTransactionMethod('all');
    }

    if (!validStatuses.includes(transactionStatus)) {
      setTransactionStatus('all');
    }
  }, [selectedTab]);

  useEffect(() => {
    if (
      selectedTab === 'swap' &&
      currencyPairsData?.currencyPairs?.length &&
      !activeCurrency
    ) {
      setActiveCurrency(currencyPairsData.currencyPairs[0]);
    } else if (currenciesData?.currencies?.length && !activeCurrency) {
      setActiveCurrency(currenciesData.currencies[0]);
    }
  }, [currenciesData, currencyPairsData, selectedTab, activeCurrency]);

  const effectiveTransactionMethod =
    selectedTab === 'withdraw' && activeTransactionMethod === 'merchantPay'
      ? 'mobilemoney'
      : activeTransactionMethod === 'all'
      ? undefined
      : activeTransactionMethod;

  const transactionsQuery = useTransactions({
    currencyId: activeCurrency?.id,
    paymentMethod: selectedTab,
    status: transactionStatus === 'all' ? undefined : transactionStatus,
    transactionType: effectiveTransactionMethod,
    pageNumber: transactionPageNumber,
  });

  const isLoading = isCurrenciesLoading || transactionsQuery.isLoading;

  return (
    <ContentLayout
      title="Transactions"
      breadcrumbs={<Breadcrumbs />}
      end={
        <div
          className={`flex items-center space-x-3 ${
            userStatus === 'active' ? '' : 'blur'
          }`}
        >
          <RefreshButton />
          <Button
            variant="glass"
            onClick={() => navigate('/app/transactions/visualizer')}
          >
            Visualizer
          </Button>
          <GlassSearchInput
            placeholder="Search for transactions"
            onChange={handleSearch}
          />
        </div>
      }
    >
      {isLoading ? (
        <SkeletonSection />
      ) : searchTerm && searchResults?.length > 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Table data={searchResults} columns={columns} />
        </motion.div>
      ) : (
        <div>
          <div className="flex flex-row items-center justify-between mb-3">
            <CurrencySelector
              activeCurrency={activeCurrency}
              setActiveCurrency={setActiveCurrency}
              selectedTab={selectedTab}
              currenciesData={currenciesData}
              currencyPairsData={currencyPairsData}
            />
            <Filters
              onDaysChange={setDaysFilter}
              setTransactionStatus={setTransactionStatus}
              setActiveTransactionMethod={setActiveTransactionMethod}
              onStatusChange={setTransactionStatus}
              transactionType={selectedTab}
              transactionStatus={transactionStatus}
              activeTransactionMethod={activeTransactionMethod}
            />
          </div>

          <TransactionTabs
            selectedTab={selectedTab}
            handleTabChange={setSelectedTab}
          />

          <TransactionsList
            transactions={transactionsQuery.data}
            status={transactionStatus}
          />
        </div>
      )}
    </ContentLayout>
  );
};

// Skeleton section component
const SkeletonSection = () => (
  <div className="space-y-6">
    <div className="flex justify-between">
      <Skeleton className="h-10 w-64 rounded-lg bg-white/10" />
      <div className="flex space-x-3">
        <Skeleton className="h-10 w-32 rounded-lg bg-white/10" />
        <Skeleton className="h-10 w-44 rounded-lg bg-white/10" />
        <Skeleton className="h-10 w-52 rounded-lg bg-white/10" />
      </div>
    </div>

    <div className="flex justify-between">
      <Skeleton className="h-10 w-72 rounded-lg bg-white/10" />
      <Skeleton className="h-10 w-80 rounded-lg bg-white/10" />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[...Array(6)].map((_, idx) => (
        <Skeleton key={idx} className="h-20 rounded-lg bg-white/10" />
      ))}
    </div>
  </div>
);
